import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Size = 'sm' | 'md';

export interface CategorySelectProps {
  value: string;
  onChange: (name: string) => void;
  options: string[];
  allowCreate?: boolean;
  allowRename?: boolean;
  onCreate?: (name: string) => void;
  onRename?: (oldName: string, newName: string) => void;
  placeholderKey?: string;
  size?: Size;
  className?: string;
  ariaLabelKey?: string;
}

/**
 * Accessible, responsive, Tailwind-styled single-select with search and optional inline create/rename.
 * No translations added: uses i18n keys only.
 */
const CategorySelect: React.FC<CategorySelectProps> = ({
  value,
  onChange,
  options,
  allowCreate = false,
  allowRename = false,
  onCreate,
  onRename,
  placeholderKey = 'productsPage.categories.select.placeholder',
  size = 'md',
  className = '',
  ariaLabelKey = 'productsPage.form.labels.category',
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const [renameMode, setRenameMode] = useState(false);
  const [renameValue, setRenameValue] = useState(value || '');

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setRenameValue(value || '');
  }, [value]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    const s = new Set<string>();
    for (const opt of options || []) {
      const name = String(opt || '');
      if (!name) continue;
      if (!q || name.toLowerCase().includes(q)) s.add(name);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [options, query]);

  const showCreateAffordance = allowCreate && (query || '').trim().length > 0 && !filtered.some(o => o.toLowerCase() === query.trim().toLowerCase());

  const sizeClasses = size === 'sm'
    ? 'px-2 py-1 text-sm'
    : 'px-3 py-2 text-sm';

  const ring = 'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';

  const submitCreate = () => {
    const n = (query || '').trim();
    if (!n) return;
    onCreate?.(n);
    onChange(n);
    setOpen(false);
    setQuery('');
    setHighlightIndex(-1);
  };

  const commitRename = () => {
    const oldName = String(value || '').trim();
    const newName = String(renameValue || '').trim();
    if (!oldName || !newName || oldName.toLowerCase() === newName.toLowerCase()) {
      setRenameMode(false);
      return;
    }
    onRename?.(oldName, newName);
    onChange(newName);
    setRenameMode(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        setTimeout(() => listRef.current?.focus(), 0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => {
        const next = Math.min((prev < 0 ? -1 : prev) + 1, filtered.length - 1 + (showCreateAffordance ? 1 : 0));
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex === -1) {
        // If nothing highlighted, select current value if present
        if (value) {
          setOpen(false);
          return;
        }
        if (showCreateAffordance) {
          submitCreate();
          return;
        }
      } else {
        const createIdx = filtered.length;
        if (showCreateAffordance && highlightIndex === createIdx) {
          submitCreate();
        } else {
          const chosen = filtered[highlightIndex];
          if (chosen) onChange(chosen);
          setOpen(false);
          setQuery('');
          setHighlightIndex(-1);
        }
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(-1);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`} aria-expanded={open} aria-haspopup="listbox">
      <label className="sr-only">{t(ariaLabelKey)}</label>
      {renameMode ? (
        <div className="flex flex-col gap-2">
          <input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); setRenameMode(false); setRenameValue(value || ''); }
            }}
            className={`w-full border border-white/20 rounded-md ${sizeClasses} ${ring}`}
            placeholder={t('productsPage.categories.actions.rename')}
          />
          <p className="text-xs text-white/50">
            {t('productsPage.categories.rename.preview', { old: String(value || ''), next: String(renameValue || '') })}
          </p>
          <p className="text-[11px] text-white/40">
            {t('productsPage.categories.rename.scopeNote')}
          </p>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={commitRename}
              className="btn-taratic btn-green w-full px-2.5 py-1.5 text-xs rounded-md"
              title={t('productsPage.categories.actions.save')}
              aria-label={t('productsPage.categories.actions.save')}
            >
              {t('productsPage.categories.actions.save')}
            </button>
            <button
              type="button"
              onClick={() => { setRenameMode(false); setRenameValue(value || ''); }}
              className="btn-taratic btn-ghost-dark w-full px-2.5 py-1.5 text-xs border rounded-md"
              title={t('productsPage.form.buttons.cancelEdit')}
              aria-label={t('productsPage.form.buttons.cancelEdit')}
            >
              {t('productsPage.form.buttons.cancelEdit')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls="category-select-listbox"
              aria-autocomplete="list"
              value={open ? query : (value || '')}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={t(placeholderKey)}
              className={`w-full border border-white/20 rounded-full bg-transparent ${sizeClasses} ${ring} pl-10 pr-4`}
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.9 14.32a8 8 0 111.414-1.414l3.386 3.387a1 1 0 01-1.415 1.414l-3.385-3.386zM8 14a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
              </svg>
            </span>

            {open && (
              <div
                id="category-select-listbox"
                ref={listRef}
                tabIndex={-1}
                role="listbox"
                className="absolute z-20 mt-2 w-full max-h-60 overflow-auto bg-gray-900/95 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl py-2"
              >
                {filtered.length === 0 && !showCreateAffordance && (
                  <div className="px-3 py-2 text-sm text-white/50">
                    {t('productsPage.categories.select.noResults')}
                  </div>
                )}
                {filtered.map((opt, idx) => {
                  const active = idx === highlightIndex;
                  return (
                    <div
                      key={opt}
                      role="option"
                      aria-selected={String(opt || '') === String(value || '')}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onClick={() => { onChange(opt); setOpen(false); setQuery(''); setHighlightIndex(-1); }}
                      className={`w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors ${active ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'}`}
                      title={opt}
                    >
                      {opt}
                    </div>
                  );
                })}
                {showCreateAffordance && (
                  <div
                    role="option"
                    onMouseEnter={() => setHighlightIndex(filtered.length)}
                    onClick={submitCreate}
                    className={`w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors ${highlightIndex === filtered.length ? 'bg-green-500/20 text-green-300' : 'text-green-400 hover:bg-green-500/10'}`}
                    title={t('productsPage.categories.select.createNew')}
                  >
                    + {t('productsPage.categories.select.createNew')}: “{(query || '').trim()}”
                  </div>
                )}
              </div>
            )}
          </div>

          {allowRename && (value || '').trim() !== '' && (
            <button
              type="button"
              onClick={() => { setRenameMode(true); setRenameValue(value || ''); }}
              className={`text-xs border rounded-md ${size === 'sm' ? 'p-2' : 'p-2.5'} hover:bg-black/10`}
              title={t('productsPage.categories.actions.rename')}
              aria-label={t('productsPage.categories.actions.rename')}
            >
              <i className="fa-solid fa-pen" aria-hidden="true"></i>
              <span className="sr-only">{t('productsPage.categories.actions.rename')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CategorySelect;